<template>
	<footer ref="el" data-component="footer">
		<section class="preorder g-row">
			<div class="g-col xxl-4 md-10 md-offset-6 sm-offset-5">
				<p class="-p-small">{{ tagline }}</p>
			</div>
			<div class="g-col xxl-8 xxl-offset-2 md-14 md-offset-6 sm-offset-5">
				<h3 class="-title2">{{ title }}</h3>
			</div>
		</section>

		<div v-if="heroImage.src" class="image-container">
			<nuxt-img class="desktop" :src="heroImage.src" :alt="heroImage.alt" />
		</div>

		<section class="footer-info g-table -p-small">
			<div class="contact-tagline">
				<h2 v-if="contactTagline" class="-p-large">
					<a
						v-if="contactTaglineLink"
						:href="contactTaglineLink"
						target="_blank"
						rel="noopener noreferrer"
						class="contact-tagline-link"
					>
						{{ contactTagline }}
					</a>
					<template v-else>{{ contactTagline }}</template>
				</h2>
			</div>

			<div class="links">
				<ul v-if="contacts.length" class="contacts">
					<li v-for="(contact, index) in contacts" :key="index">
						<div class="opacity-40">{{ contact.purpose }}</div>
						<HyperLink v-if="contact.link" class="hover-opacity" :to="contact.link">
							{{ contact.label }}
						</HyperLink>
						<span v-else>{{ contact.label }}</span>
					</li>
				</ul>

				<ul v-if="socials.length" class="socials">
					<li v-for="(social, index) in socials" :key="index">
						<a
							v-if="social?.url"
							:href="social.url"
							target="_blank"
							rel="noopener noreferrer"
							class="hover-opacity"
							:aria-label="`Open ${social.label}`"
						>
							{{ social.label }}
						</a>
						<span v-else class="social-spacer" aria-hidden="true">&nbsp;</span>
					</li>
				</ul>
			</div>

			<ul v-if="studios.length" class="studios">
				<li v-for="(studio, index) in studios" :key="index">
					<a
						:href="studio.url"
						target="_blank"
						rel="noopener noreferrer"
						aria-label="Open Location"
					>
						<div class="opacity-40">{{ studio.address }}</div>
					</a>
					<div class="studio-gps">
						<div>GPS</div>
						<a
							:href="studio.url"
							target="_blank"
							rel="noopener noreferrer"
							class="hover-opacity"
							aria-label="Open Location"
						>
							<div>{{ studio.coordinates }}</div>
						</a>
					</div>
				</li>
			</ul>
		</section>
	</footer>
</template>

<script setup>
import { HyperLink } from '~/components/core'

defineOptions({
	name: 'SiteFooter',
})

defineProps({
	heroImage: {
		type: Object,
		default: () => ({
			src: '',
			alt: '',
		}),
	},
	tagline: { type: String, default: '' },
	title: { type: String, default: '' },
	contactTagline: { type: String, default: '' },
	contactTaglineLink: { type: String, default: '' },
	contacts: { type: Array, default: () => [] },
	socials: { type: Array, default: () => [] },
	studios: { type: Array, default: () => [] },
})

const el = ref(null)
</script>

<style lang="scss" scoped>
[data-component='footer'] {
	position: relative;

	color: $white;
	background-color: $black;

	padding-top: rs(71, 51);

	white-space: pre-line;

	.-p-small,
	.-p-large,
	.-title2 {
		color: $white;
		white-space: pre-line;
	}

	.-p-large {
		span,
		a {
			@include p_large;
		}
	}

	a,
	div,
	h2,
	h3,
	li,
	p,
	span {
		color: $white;
	}

	.opacity-40 {
		opacity: 0.4;
	}

	.-p-small {
		@include max-width($xs) {
			margin-bottom: rs(20);
		}
	}

	.-title2 {
		@include max-width($xs) {
			margin-bottom: rs(120);
		}
	}

	.image-container {
		:deep(img) {
			display: block;
			width: 100%;
			height: auto;

			@include max-width($xs) {
				object-position: 4% 50%;
			}
		}
	}

	.footer-info {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;

		padding-bottom: rs(150, 130);

		.contact-tagline {
			grid-column: 7 / span 16;
			grid-row: 1;

			margin-bottom: rs(96, 64);

			.contact-tagline-link {
				display: inline-block;
				width: fit-content;
			}
		}

		.links {
			grid-column: 7 / span 6;
			grid-row: 2;

			.contacts li:not(:first-child),
			.socials {
				margin-top: rs(24, 16);
			}

			.social-spacer {
				display: block;
			}
		}

		.studios {
			grid-column: 17 / span 8;
			grid-row: 2;

			display: grid;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: inherit;

			@include max-width($md) {
				grid-column: 15 / span 10;
			}

			& > li {
				padding-right: column-width(1);
			}

			.studio-gps {
				margin-top: 1.2em;
			}
		}

		@include max-width($sm) {
			.contact-tagline,
			.links {
				grid-column: 6 / span 17;
				margin-bottom: 64px;
			}

			.studios {
				grid-row: 3;
				grid-column: 6 / span 17;
			}
		}
	}

	@include max-width($xs) {
		display: grid;
		grid-template-areas:
			'preorder'
			'footer-stack';
		grid-template-rows: auto auto;

		.preorder {
			grid-area: preorder;
			flex-direction: column;

			& > .g-col {
				width: 100%;
				max-width: 100%;
				flex: 0 0 auto;
				margin-left: 0;
			}
		}

		.image-container,
		.footer-info {
			grid-area: footer-stack;
		}

		.image-container {
			position: relative;
			z-index: 0;
			align-self: stretch;
			overflow: hidden;

			:deep(img) {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
		}

		.footer-info {
			display: flex;
			flex-direction: column;
			gap: 64px;
			position: relative;
			z-index: 1;
			bottom: auto;
			left: auto;
			align-self: start;

			.contact-tagline,
			.links,
			.studios {
				grid-row: auto;
				grid-column: auto;
				margin-bottom: 0;
			}

			.studios {
				display: flex;
				flex-direction: column;
				gap: 40px;

				& > li {
					padding-right: 0;
				}
			}
		}
	}
}
</style>
